import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { TID } from "@/data/site";

const GOOGLE_PROFILE_URL = "https://g.page/r/CZQ7oXVCLYyNEBE";

// Section ids in **document order** as they appear in Home.jsx — used by the
// scrollspy. Document order: Hero(home) → Pricing(transfers) → WaitingPolicy(waiting)
// → Tours(tours) → Features(services) → Contact(contact).
// When the user is at the very top (Hero / "home"), we visually highlight the
// "Transfers" pill since that's the first menu item and there is no "home" pill.
const SECTION_IDS = ["home", "transfers", "waiting", "tours", "services", "contact"];

const Nav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("transfers");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = pathname === "/";

  // Scrollspy — manual scroll listener that picks the section whose top edge
  // is just above the viewport's "active line" (40% from the top). More
  // reliable than IntersectionObserver for short sections that don't fill
  // the threshold band. When the user is at the very top of the page (Hero),
  // we highlight the "Transfers" pill since "home" has no dedicated menu item.
  useEffect(() => {
    if (!isHome) return;
    const onScroll = () => {
      const activeLine = window.scrollY + window.innerHeight * 0.4;
      let current = "transfers";
      for (const id of SECTION_IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.offsetTop;
        if (top <= activeLine) {
          // "home" → visually show as "transfers" (no home pill in the menu).
          current = id === "home" ? "transfers" : id;
        }
      }
      setActiveSection(current);
    };
    onScroll(); // initial highlight
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  const links = [
    { id: "transfers", label: "Transfers", anchor: "#transfers", tid: TID.navServices },
    { id: "tours", label: "Tours", anchor: "#tours", tid: TID.navTours },
    { id: "services", label: "Services", anchor: "#services", tid: "nav-our-services" },
    { id: "waiting", label: "Waiting", anchor: "#waiting", tid: "nav-waiting" },
    { id: "contact", label: "Contact", anchor: "#contact", tid: "nav-contact" },
  ];

  const handleNav = (e, anchor, id) => {
    e.preventDefault();
    if (!isHome) {
      navigate(`/${anchor}`);
      return;
    }
    setActiveSection(id);
    if (id === "home") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 transition-all duration-500 z-50 ${
        scrolled
          ? "py-1.5 backdrop-blur-xl bg-[#0a1830]/90 border-b border-white/5"
          : "py-1 bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        {/* Top row — logo + brand title + Google reviews */}
        <div className="flex items-center justify-between gap-3 lg:gap-6">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              if (isHome) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                navigate("/");
              }
            }}
            data-testid={TID.navHome}
            className="flex items-center group shrink-0"
          >
            <img
              src="/brand/logo.png"
              alt="Athens Airport Taxi Transfers"
              className={`w-auto select-none transition-all duration-500 group-hover:scale-105 ${
                scrolled ? "h-9 md:h-12" : "h-10 sm:h-14 md:h-20"
              }`}
              draggable="false"
            />
          </a>

          <h1
            data-testid="brand-title"
            className={`flex-1 font-display font-light text-foreground leading-tight tracking-tight transition-all ${
              scrolled
                ? "text-xs sm:text-base lg:text-xl"
                : "text-sm sm:text-base lg:text-2xl xl:text-3xl"
            }`}
          >
            Athens Airport <span className="text-gold italic font-normal">Taxi</span> Transfers
          </h1>

          {/* Google reviews — compact button on the right (hidden on small screens) */}
          <a
            href={GOOGLE_PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-google-reviews-btn"
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] hover:border-gold/40 hover:bg-gold/[0.06] px-3 py-1.5 transition-all shrink-0"
          >
            <span className="text-foreground font-semibold text-xs">5.0</span>
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-[#e6b75c] text-gold" strokeWidth={1.5} />
              ))}
            </div>
            <span className="text-[0.68rem] text-foreground/55 hidden lg:inline">Google reviews</span>
            <ExternalLink className="h-3 w-3 text-foreground/40" />
          </a>
        </div>

        {/* Inline menu — tucked right under the brand title, right-aligned and
            compact so the labels never clip the Acropolis hero image. */}
        <nav
          data-testid="nav-links"
          className="mt-0 flex flex-nowrap items-center justify-end gap-0.5 sm:gap-1.5 lg:gap-2"
          aria-label="Primary"
        >
          {links.map((l) => {
            const active = isHome && activeSection === l.id;
            return (
              <a
                key={l.id}
                href={l.anchor}
                data-testid={l.tid}
                onClick={(e) => handleNav(e, l.anchor, l.id)}
                className={`shrink-0 rounded-full px-1.5 sm:px-3.5 py-0.5 sm:py-1.5 text-[0.55rem] sm:text-sm font-medium transition-all whitespace-nowrap ${
                  active
                    ? "bg-gold text-[#0a1830] shadow-md shadow-gold/30"
                    : "text-foreground/75 hover:text-gold hover:bg-white/5"
                }`}
              >
                {l.label}
              </a>
            );
          })}
        </nav>
      </div>
    </header>
  );
};

export default Nav;
